import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';

import AvailableSlotsView from './view';
import styled from 'styled-components';
import { FlexView, Text } from '@cuemath/comps';
import { epochToTime } from 'utils';

const HeaderWrapper = styled.div`
  width: 100%;
`;

const daysName = ['Mon', 'Tues', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const TextHeader = styled(Text)`
  font-weight: 600;
  text-size: 10px;
  text-align: center;
  width: 100%;
`;

export default class TeacherBatch extends PureComponent {
  static defaultProps = {
    uniqueSlots: null,
    batchView: false,
  };

  static propTypes = {
    uniqueSlots: PropTypes.shape({}).isRequired,
    teacher: PropTypes.object.isRequired,
    batchView: PropTypes.bool.isRequired,
  };

  constructor(props) {
    super(props);
    const { uniqueSlots } = this.props;

    this.state = {
      tableState: null,
      uniqueSlotsState: uniqueSlots,
    };
  }

  componentDidUpdate(prevProps, prevState) {
    const { uniqueSlots } = this.props;
    const { uniqueSlots: prevUniqueSlot } = prevProps || {};

    if (uniqueSlots !== prevUniqueSlot) {
      this.setState({
        uniqueSlotsState: uniqueSlots,
      });
    }
  }

  getDayHeader = value => {
    return (
      <HeaderWrapper>
        <FlexView>
          <TextHeader>{value}</TextHeader>
        </FlexView>
      </HeaderWrapper>
    );
  };

  getHeader = (startTime, endTime) => {
    return (
      <HeaderWrapper>
        <FlexView>
          <TextHeader>{startTime}</TextHeader>
          <TextHeader>{endTime}</TextHeader>
        </FlexView>
      </HeaderWrapper>
    );
  };

  // state assignment
  assignTableState = uniqueSlots => {
    /**
     * @param ( type: Object)
     *
     * * Expecting an object with 'all_timings', 'batch_details' and 'batch_timings' keys.
     *
     * -> Create a table with n rows and 8 columns.
     *   - n depends on 'all_slot_timings'
     * -> Ist row shows week.
     * -> Ist column shows time.
     * -> Table shows slots created for classes.
     *
     * -> columnData consists of weekdays as title.
     *   -> columnData = [ { data } ].
     *
     * -> rowData consists of class info scheduled for a particular day at particular time.
     *   -> rowData = { <week day no>: data }
     *     -> data = { key: unique string, value: cellValue }
     *
     * * Return object consisting table and column data.
     *
     * -> { data: tableData columns: columnData }
     *
     * @return ( type: Object )
     */

    const { teacherBigForm } = this.props;
    const tableData = [];
    const { all_slot_timings: allSlotTimings = [], slots_details: slotDetails = {} } =
      uniqueSlots || {};

    const columnData = [
      {
        Header: this.getHeader('Time', 'Day'),
        accessor: 'time',
        minWidth: teacherBigForm ? 120 : 70,
        minHeight: 20,
        maxWidth: teacherBigForm ? 200 : 100,
      },
    ];

    // header bind
    for (let col = 1; col <= 7; col++) {
      columnData.push({
        Header: this.getDayHeader(daysName[col - 1]),
        accessor: col.toString(),
        minWidth: teacherBigForm ? 120 : 70,
        minHeight: 20,
        maxWidth: teacherBigForm ? 200 : 100,
      });
    }

    for (let row = 1; row <= allSlotTimings.length; row++) {
      let rowData = {};
      const currentSlotTime = allSlotTimings[row - 1] || '';
      const [alternateStartTime, alternateEndTime] = currentSlotTime.split('_') || [];

      for (let col = 0; col < 8; col++) {
        const cellKey = `'cell-'${col}'-'${row}`;
        const cellValues = [];
        let rowKey = col.toString();
        const dailyBatchSlots = slotDetails[col] || {};

        const slotTimingInfo = dailyBatchSlots[currentSlotTime] || {};
        const { start_time: startTime, end_time: endTime } = slotTimingInfo || {};
        const slotStartTime = startTime || alternateStartTime;
        const slotEndTime = endTime || alternateEndTime;

        if (!col) {
          rowKey = 'time';
          cellValues.push({
            value: this.getHeader(epochToTime(slotStartTime), epochToTime(slotEndTime)),
            isSelectable: false,
            isSelected: false,
            isVisible: true,
          });
        } else if (dailyBatchSlots) {
          if (currentSlotTime in dailyBatchSlots) {
            cellValues.push({
              cellkey: cellKey,
              value: dailyBatchSlots[currentSlotTime],
              isSelectable: true,
              isSelected: false,
              isVisible: true,
            });
          } else {
            cellValues.push({
              cellkey: cellKey,
              value: '',
              isSelectable: false,
              isSelected: false,
              isVisible: false,
            });
          }
        }

        rowData[rowKey] = {
          key: cellKey,
          value: cellValues,
        };
      }

      tableData.push(rowData);
    }

    return {
      data: tableData,
      columns: columnData,
    };
  };

  render() {
    const { uniqueSlotsState } = this.state;
    const { batchView, teacher } = this.props;
    const { all_slot_timings: allSlotTimings } = uniqueSlotsState || {};

    const newTableState = this.assignTableState(uniqueSlotsState);
    let rowCount = 0;

    if (allSlotTimings) {
      rowCount = allSlotTimings.length;
    }

    if (newTableState) {
      return (
        <AvailableSlotsView
          tableState={newTableState}
          batchView={batchView}
          teacher={teacher}
          all_timings={rowCount}
        />
      );
    }

    return null;
  }
}
